import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { AuthStore, splashHidden } from '@huddle-sports-platform/app-core';
import {
  HudBootGate,
  HudBootGateVeil,
  HudSpinner,
  type HudBootMark,
} from '@huddle-sports-platform/ui';

import { crestReady } from './core/crest';
import { ClubBrandStore } from './data/club-brand';
import { DataInit } from './data/data-init';
import { DoorExit } from './data/door-exit';

/**
 * The veil's minimum time on screen, matching the native splash's
 * `SPLASH_MINIMUM_MS` in main.ts.
 *
 * A club's crest over its colours IS the launch - the same moment the native
 * splash gives - and a boot that resolves in 200ms turns it into a flinch:
 * something appears, and is gone before it reads as anything. Native holds
 * its splash for this exact reason; nothing held the veil, so it kept the
 * beat only when the network was slow.
 *
 * It applies to EVERY arming of the veil, not just the launch: signing in
 * re-arms it (see the constructor), and that moment - the crest, then the
 * member's first screen - is the same brand beat as a cold start.
 */
export const VEIL_FLOOR_MS = 2_000;

/**
 * The root shell. The boot-gate veil (the data-initialisation cover: the
 * animated mark over the bare background) lives HERE, above the router -
 * so it is the FIRST thing on screen for EVERY launch, before any route
 * (the doors, /complete-your-profile, the tab shell) gets a frame.
 *
 * Readiness is presence-aware, which is why it is a bespoke computed and
 * not app-core's bootReadiness (that helper can't express the either/or):
 * - signed OUT: the doors need no data - auth resolution alone lifts it.
 * - signed IN: hold until DATA INITIALISATION is done - the whole up-front
 *   batch in `data/data-init.ts` (one registry; add loaders there). Every
 *   routing decision beneath the veil reads that data, so nothing can
 *   flash a wrong state while it loads.
 */
@Component({
  selector: 'app-root',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonApp, IonRouterOutlet, HudBootGateVeil, HudSpinner],
  template: `
    <ion-app>
      <ion-router-outlet />
      @if (awaitingClub()) {
        <!-- A club's page, brand not known yet: NOBODY's mark, just a
             spinner. See the class note - this is the only cover that can
             honestly show while we still have to ask whose app this is. -->
        <div class="club-wait" role="status" aria-label="Loading">
          <hud-spinner size="lg" />
        </div>
      } @else {
        <hud-boot-gate
          [done]="gateDone()"
          [covered]="!splash()"
          [mark]="bootMark()"
          [label]="bootLabel()"
          [busyLabels]="BOOT_PHASES"
        />
      }
    </ion-app>
  `,
  styles: [
    `
      /* Above the veil's own layer, and the same bare background the veil
         uses - the handover between the two is invisible. The spinner is
         deliberately drained of the accent: an accent is a brand, and we do
         not know whose yet. */
      .club-wait {
        position: fixed;
        inset: 0;
        z-index: 52;
        display: grid;
        place-items: center;
        background: var(--bg);
        --accent: var(--fg-muted);
      }
    `,
  ],
})
export class App {
  private readonly auth = inject(AuthStore);
  private readonly dataInit = inject(DataInit);
  private readonly doorExit = inject(DoorExit);
  private readonly gate = inject(HudBootGate);
  /** The active club brand - null is the shared Huddle app. */
  protected readonly clubBrand = inject(ClubBrandStore);

  /** The door's cycling words - what the boot is actually doing, in
   *  order. Most boots finish inside the first phase; a slow network
   *  walks through the rest (5s a line, the stage's own cadence). */
  protected readonly BOOT_PHASES = ['Signing in', 'Loading data', 'Almost done'] as const;

  /** The accessible name for the wait (and the Huddle loader branch's
   *  aria label on the brandless dev build). */
  protected readonly bootLabel = computed(() => {
    const brand = this.clubBrand.brand();
    return brand ? `Loading ${brand.appName}` : 'Loading Huddle';
  });

  /** The veil's mark: a branded build shows the club's crest (or colours),
   *  never Huddle's goalposts. Computed, so the veil's input keeps its
   *  identity across change detection. */
  protected readonly bootMark = computed<HudBootMark | null>(() => {
    const brand = this.clubBrand.brand();
    return brand
      ? {
          logo: brand.logoUrl,
          primary: brand.primaryColour,
          secondary: brand.secondaryColour,
          alt: brand.appName,
        }
      : null;
  });

  /**
   * A club's page whose brand has not landed yet.
   *
   * The kit's veil wears HUDDLE's animated mark when it has no club mark to
   * show, which is right for the shared app and wrong here: on
   * `dublinraiders.huddlesports.ie` it put Huddle's goalposts on screen for
   * the length of one lookup, and the member watched somebody else's brand
   * turn into theirs. A cache hit paints the club immediately (brand() is
   * already set), so this only covers the genuinely unknown moment.
   */
  protected readonly awaitingClub = computed(
    () => !this.clubBrand.settled() && !this.clubBrand.brand(),
  );

  /** The native splash state - `!splash()` feeds the veil's `covered`. */
  protected readonly splash = splashHidden;

  /** The veil's minimum beat has been served (see {@link VEIL_FLOOR_MS}). */
  private readonly beatServed = signal(false);
  private beatTimer: ReturnType<typeof setTimeout> | undefined;

  /** By uid, not user object - token refreshes re-emit the same uid and
   *  must not re-arm the veil mid-session. */
  private readonly uid = computed(() => this.auth.user()?.uid ?? null);

  protected readonly gateDone = computed(() => {
    // The veil is allowed to be seen, however fast the work behind it was.
    if (!this.beatServed()) return false;
    // Which club this is, if any, is settled before anything paints: a door
    // must not appear unbranded and become a club's a moment later.
    // (Synchronous on native and in the shared app; one lookup on a club
    // URL - see data/club-brand.)
    if (!this.clubBrand.settled()) return false;
    // The veil is the web's splash: it must not lift onto a door whose crest
    // is still decoding. Capped in crest.ts, so it cannot hang the app.
    if (!crestReady()) return false;
    if (!this.auth.ready()) return false;
    if (!this.uid()) return true;
    // A signed-in member's DATA INITIALISATION - the whole up-front batch
    // (account doc, invitations, admin claim; the registry lives in
    // data/data-init.ts). Every routing decision beneath the veil reads
    // this data, so the veil lifts onto a SETTLED first page rather than
    // onto guards still deciding.
    return this.dataInit.ready();
  });

  constructor() {
    // The club brand is adopted by the `clubBrand` field above: constructing
    // the store is what applies the theme override, and the field puts that
    // BEFORE the first routed page paints (native flavours bake the brand;
    // on the web the store resolves the slug the app is mounted under).

    // A fresh page load always gates; a mid-session sign-in re-arms it -
    // the new account's data-init deserves the same cover as a cold
    // launch. (Sign-OUT does not: the sign-in door needs no veil.)
    this.gate.show();
    this.armBeat();
    effect(() => {
      if (!this.uid()) return;
      // A door mid-dissolve owns this entrance (data/door-exit.ts): its
      // content fades to the bare background FIRST, and the veil arms on
      // its release - reading holding() here re-runs the effect then.
      if (this.doorExit.holding()) return;
      // A QUIET release skips the veil entirely: the door stayed on
      // screen through the whole wait (data-init included) and hands
      // over with an ordinary page transition. Consumed once.
      if (untracked(() => this.doorExit.consumeQuietRelease())) return;
      this.gate.show();
      this.armBeat();
    });

    inject(DestroyRef).onDestroy(() => clearTimeout(this.beatTimer));
  }

  /**
   * Start the veil's minimum beat for the arming that just happened.
   *
   * Skipped only while the NATIVE SPLASH is still up: it is already holding
   * the same 2s over the same lockup, and stacking a second wait behind it
   * would just make the launch feel slow. Every other arming - the web
   * launch, and a sign-in on either platform - gets the beat.
   */
  private armBeat(): void {
    clearTimeout(this.beatTimer);
    if (!splashHidden()) {
      this.beatServed.set(true);
      return;
    }
    this.beatServed.set(false);
    this.beatTimer = setTimeout(() => this.beatServed.set(true), VEIL_FLOOR_MS);
  }
}
